import React from 'react';
import PrintLayout from './PrintLayout';

function msToTime(ms) {
  if (!ms) return '0:00';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h > 0) return `${h}:${String(min).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${min}:${String(sec).padStart(2,'0')}`;
}

const svgStyle = { display: 'block', background: 'transparent', overflow: 'visible' };

const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#5f7942" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={svgStyle}>
    <rect x="9" y="2" width="6" height="12" rx="3" fill="none"/>
    <path d="M5 10a7 7 0 0 0 14 0" fill="none"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
    <line x1="9" y1="21" x2="15" y2="21"/>
  </svg>
);

const ExchangeIcon = () => (
  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#5f7942" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={svgStyle}>
    <path d="M17 1l4 4-4 4" fill="none"/>
    <path d="M3 11V9a4 4 0 0 1 4-4h14" fill="none"/>
    <path d="M7 23l-4-4 4-4" fill="none"/>
    <path d="M21 13v2a4 4 0 0 1-4 4H3" fill="none"/>
  </svg>
);

const MuteIcon = () => (
  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#5f7942" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={svgStyle}>
    <line x1="1" y1="1" x2="23" y2="23"/>
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12" fill="none"/>
    <path d="M15 9.34V4a3 3 0 0 0-5.94-.6" fill="none"/>
    <path d="M17 16.95A7 7 0 0 1 5 12v-2" fill="none"/>
    <path d="M19 12a7 7 0 0 0-.11-1.17" fill="none"/>
    <line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="9" y1="23" x2="15" y2="23"/>
  </svg>
);

export default function PrintAwards({ pageNumber, awards, portrait }) {
  const longestComments = awards?.longestComments || (awards?.longestComment ? [awards.longestComment] : []);
  const longestMutes = awards?.longestMutes || (awards?.longestMute ? [awards.longestMute] : []);

  const rankStyle = { fontSize: 9, fontWeight: 700, color: '#9ca3af', minWidth: 14 };
  const nameStyle = { fontSize: 13, fontWeight: 700, color: '#1e2e45', flex: 1 };
  const timeStyle = { fontSize: 11, fontWeight: 600, color: '#5f7942', whiteSpace: 'nowrap' };

  return (
    <PrintLayout pageNumber={pageNumber}>
      <div style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, padding: '5% 6%', background: '#f5f4f0' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700, color: '#1e2e45', marginBottom: 32 }}>
          Notable Moments
        </div>
        <div style={{ display: 'flex', flexDirection: portrait ? 'column' : 'row', gap: 24 }}>
          {/* Longest Comments */}
          <div style={{ flex: 1, background: 'white', borderRadius: 10, padding: '24px 20px', border: '1px solid rgba(42,61,91,0.15)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <MicIcon />
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', fontFamily: 'Montserrat, sans-serif' }}>
              Longest Comments
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {longestComments.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={rankStyle}>{i + 1}.</span>
                  <span style={nameStyle}>{item.speaker?.split('@')[0] || item.speaker}</span>
                  <span style={timeStyle}>{msToTime(item.ms)}</span>
                </div>
              ))}
              {longestComments.length === 0 && <div style={{ fontSize: 11, color: '#9ca3af' }}>—</div>}
            </div>
          </div>

          {/* Longest on Mute */}
          <div style={{ flex: 1, background: 'white', borderRadius: 10, padding: '24px 20px', border: '1px solid rgba(42,61,91,0.15)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <MuteIcon />
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', fontFamily: 'Montserrat, sans-serif' }}>
              Longest on Mute
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {longestMutes.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={rankStyle}>{i + 1}.</span>
                  <span style={nameStyle}>{item.speaker?.split('@')[0] || item.speaker}</span>
                  <span style={timeStyle}>{msToTime(item.ms)}</span>
                </div>
              ))}
              {longestMutes.length === 0 && <div style={{ fontSize: 11, color: '#9ca3af' }}>—</div>}
            </div>
          </div>

          {/* Most Back-and-Forth */}
          <div style={{ flex: 1, background: 'white', borderRadius: 10, padding: '24px 20px', border: '1px solid rgba(42,61,91,0.15)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <ExchangeIcon />
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', fontFamily: 'Montserrat, sans-serif' }}>
              Most Back-and-Forth
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1e2e45', lineHeight: 1.3 }}>
              {awards?.longestExchange?.pair?.split(' ↔ ').join('\n↔ ') || '—'}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#5f7942' }}>
              {awards?.longestExchange?.turns || 0} turns
              {awards?.longestExchange?.ms ? ` · ${msToTime(awards.longestExchange.ms)}` : ''}
            </div>
          </div>
        </div>
      </div>
    </PrintLayout>
  );
}
